import { FC, useState, useRef, useEffect, HTMLAttributes } from "react";
import clsx from "classnames";
import Slider from "react-slick";
import { map } from "lodash-es";
import { Image } from "@onmo/ui";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { HOME_SLIDE_SETTINGS } from "../../../constants/slide";
import { useStyles } from "./BannerSlides.style";
import {
  FeaturedCard,
  LinkFeaturedCardData,
  LinkType,
  PathFeaturedCardData,
  Query,
} from "@onmo/graphql/graphql";
import { featuredCardsGql } from "../../../graphql";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

interface Props {
  setIframeUrl: (url: string) => void;
}

export const BannerSlides: FC<HTMLAttributes<HTMLDivElement> & Props> = (
  props,
) => {
  const classes = useStyles();
  const { setIframeUrl } = props;
  const navigate = useNavigate();
  const { className, ...otherProps } = props;
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideRef = useRef<Slider>(null);
  const slideContentRef = useRef<HTMLDivElement>(null);
  const { data: featuredCardsData } = useQuery<Query>(featuredCardsGql);
  const featuredCardsItem = featuredCardsData?.featuredCards.items || [];

  const slideSetting = {
    ...HOME_SLIDE_SETTINGS,
    afterChange: (currentSlide: number) => setCurrentSlide(currentSlide),
    infinite: featuredCardsItem?.length > 1,
  };

  useEffect(() => {
    const onPlay = () => {
      slideRef?.current?.slickPlay?.();
    };
    const onPause = () => {
      slideRef?.current?.slickPause?.();
    };
    slideContentRef?.current?.addEventListener("touchstart", onPause);
    slideContentRef?.current?.addEventListener("touchend", onPlay);
    return () => {
      slideContentRef?.current?.removeEventListener("touchstart", onPause);
      slideContentRef?.current?.removeEventListener("touchend", onPlay);
    };
  }, []);

  const onClickBanner = (featuredCard: FeaturedCard) => {
    if (featuredCard.linkType === LinkType.ExternalWindow) {
      window.open(
        (featuredCard?.data as LinkFeaturedCardData)?.redirectUrl,
        "_blank",
      );
    } else if (featuredCard.linkType === LinkType.InternalLink) {
      navigate((featuredCard?.data as PathFeaturedCardData)?.internalPath);
    } else {
      setIframeUrl((featuredCard?.data as LinkFeaturedCardData)?.redirectUrl);
    }
  };

  return (
    <div
      {...otherProps}
      className={clsx(className, classes.root)}
      ref={slideContentRef}
    >
      <Slider {...slideSetting} ref={slideRef}>
        {map(featuredCardsItem, (banner, idx) => (
          <Image
            src={banner.imageUrl?.en}
            key={idx}
            className={classes.contentImage}
            showloading
            onClick={() => onClickBanner(banner)}
          />
        ))}
      </Slider>
      {featuredCardsItem?.length > 1 && (
        <div className={clsx(classes.dots)}>
          {map(featuredCardsItem, (_, idx) => (
            <div
              key={idx}
              className={clsx(
                classes.dot,
                `${currentSlide === idx ? "active" : ""}`,
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};
